import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, TextField, Button, IconButton, MenuItem, Select, FormControl, InputLabel, SelectChangeEvent, OutlinedInput } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './serviceFormModal.module.scss';
import countries from '../../constants/countries.json';
import { formatRut, validateRut, clean } from '../../helpers/app';
import { useToast } from '../../helpers/hooks';
import { useAuthModal } from '../../context/AuthModalContext';

interface ServiceFormModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  onSubmit: (formData: any, brand?: any) => void;
  user: any;
  setLoginRedirection: any;
  brand?: any;
}

const ServiceFormModal: React.FC<ServiceFormModalProps> = ({
  open,
  onClose,
  title,
  description,
  user,
  setLoginRedirection,
  onSubmit,
  brand
}) => {
  const pushToast = useToast();
  const { handleOpenAuthModal } = useAuthModal();

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    areaCode: '+56',
    rut: ''
  });

  // Initialize form data with user information when available
  useEffect(() => {
    if (open) {
      if (user) {
        setFormData({
          nombre: user.name || '',
          apellido: user.last_name || '',
          email: user.email || '',
          telefono: user.phone?.number || '',
          areaCode: user.phone?.code || '+56',
          rut: user.legal_id || ''
        });
      } else {
        // Reset form when opening without user
        setFormData({
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          areaCode: '+56',
          rut: ''
        });
      }
    }
  }, [open, user]);

  const [errors, setErrors] = useState({
    rut: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRutChange = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9kK.-]/g, '');
    
    setFormData(prev => ({
      ...prev,
      rut: sanitizedValue
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name || 'areaCode']: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only validate RUT if user is not logged in or if they've changed it
    if (!user && formData.rut && !validateRut(formData.rut)) {
      setErrors(prev => ({ ...prev, rut: true }));
      return;
    }

    // Prepare the form data with user's legal_id if available
    const submissionData = {
      ...formData,
      rut: user?.legal_id || formData.rut // Use user's legal_id if available
    };

    onSubmit(submissionData, brand);
    onClose();
    pushToast('Formulario enviado con éxito');
  };

  const handleLoginRedirection = () => {
    handleOpenAuthModal();
  }


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: user ? styles.dialogPaperUser : styles.dialogPaper }}
    >
      <DialogTitle className={styles.dialogTitle}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          className={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <p className={styles.description}>{description} <span role="img" aria-label="smile">😊</span></p>

        <div className={styles.loginOption}>
          {!user && (
            <Button className={styles.loginButton} onClick={() => handleLoginRedirection()}>
              Ingresar con mi cuenta
            </Button>
          )}
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <TextField
            fullWidth
            label="Nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleInputChange}
            variant="outlined"
            margin="normal"
            required
            className={styles.textField}
          />

          <TextField
            fullWidth
            label="Apellido"
            name="apellido"
            value={formData.apellido}
            onChange={handleInputChange}
            variant="outlined"
            margin="normal"
            required
            className={styles.textField}
          />

          <TextField
            fullWidth
            label="Mail"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            variant="outlined"
            margin="normal"
            required
            className={styles.textField}
          />

          <FormControl fullWidth className={styles.formControl} margin="normal">
            <InputLabel>RUT</InputLabel>
            <OutlinedInput
              value={formData.rut}
              onChange={(e) => handleRutChange(e.target.value)}
              onBlur={() => {
                const formattedValue = formatRut(clean(formData.rut));
                setFormData(prev => ({
                  ...prev,
                  rut: formattedValue
                }));

                setErrors(prev => ({
                  ...prev,
                  rut: formData.rut ? !validateRut(formData.rut) : false
                }));
              }}
              label="RUT"
              placeholder=""
              error={errors.rut}
            />
            {errors.rut && <p className={styles.errorMessage}>Ingresa un RUT válido</p>}
          </FormControl>

          <div className={styles.phoneContainer}>
            <div className={styles.countryCode}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="country-code-label">Código de país</InputLabel>
                <Select
                  labelId="country-code-label"
                  name="areaCode"
                  value={formData.areaCode}
                  label="Código de país"
                  onChange={handleSelectChange}
                  className={styles.countrySelect}
                >
                  {countries.map((country: any) => (
                    <MenuItem key={country.code} value={country.dial_code}>
                      <span role="img" aria-label={country.name}>{country.flag}</span> ({country.dial_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <TextField
              fullWidth
              label="Celular"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
              variant="outlined"
              margin="normal"
              required
              className={styles.phoneField}
              type="tel"
              InputProps={{
                style: { height: '56px' }
              }}
            />
          </div>

          <div className={styles.buttonContainer}>
            <Button
              variant="outlined"
              onClick={onClose}
              className={styles.cancelButton}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              type="submit"
              className={styles.submitButton}
            >
              Enviar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog >
  );
};

export default ServiceFormModal;
